// BDSJEA-371 (Historial, incremento 2): helpers PUROS (sin React) que convierten
// el texto libre de `motivo` de los CSV de resultado en CLAVES CATEGORICAS
// CERRADAS, para graficos y filtros.
//
// Regla de seguridad: ningun valor de salida es texto libre. Lo que sale de
// estos helpers es una clave de una tabla cerrada (CLAVES_POR_CONTEXTO), una
// etiqueta de una tabla cerrada (ETIQUETAS_CLAVE) o un numero. Un motivo con
// host, URL, CUIT o nombre propio nunca llega a una etiqueta: o coincide con un
// patron conocido (y se muestra la etiqueta fija) o cae en 'OTRO'.
//
// Origen de cada clave: ver CLAVES_POR_CONTEXTO (f-string del migrador
// migracion-bsj/app/*.py y categorias que normaliza el backend para ARCA).
import { clasificarResultado } from "./migracionEstadoHelpers"

export const CONTEXTOS = Object.freeze([
  "productores",
  "establecimientos",
  "arca",
  // BDSJEA-441: actividades de la importación por niveles.
  "siembras",
  "ganaderia",
  "tambo",
  // BDSJEA-468: stock agrícola (s_* de plan-productivo, QQ).
  "stockAgricola",
])

// BDSJEA-441: contextos de las actividades (vocabularios cerrados por paso).
export const CONTEXTOS_ACTIVIDADES = Object.freeze([
  "siembras",
  "ganaderia",
  "tambo",
  "stockAgricola",
])

export const CLAVE_OTRO = "OTRO"
export const CLAVE_OTROS_MOTIVOS = "OTROS_MOTIVOS"
export const TOP_N_POR_DEFECTO = 4
export const ETIQUETA_OTROS_MOTIVOS = "Otros motivos"

// Etiquetas legibles: tabla CERRADA. Clave desconocida => "Otros".
const ETIQUETAS_CLAVE = Object.freeze({
  // Comunes / productores
  REPETIDO_OTRA_ENTIDAD: "Repetido en otra entidad",
  REPETIDO_MISMA_ENTIDAD: "Repetido en la misma entidad",
  SIN_RAZON_SOCIAL: "Sin razón social",
  CUIT_NO_CARGADO: "CUIT no cargado",
  CUIT_INVALIDO: "CUIT inválido",
  ENTIDAD_INVALIDA: "Entidad inválida",
  YA_EXISTIA: "Ya existía",
  ACTUALIZADO: "Actualizado",
  ERROR: "Error",
  // Establecimientos
  SALTADO_EXISTENTE: "Ya existía (saltado)",
  CLIENTE_NO_ENCONTRADO: "Cliente no encontrado",
  PLAN_NO_ENCONTRADO: "Plan no encontrado",
  CLIENTE_EXCLUIDO: "Cliente excluido de la migración",
  SIN_PRODUCTOR_MIGRADO: "Sin productor migrado",
  PRODUCTOR_SIN_ENTIDAD: "Productor sin entidad",
  PROVINCIA_NO_RESUELTA: "Provincia no resuelta",
  SUPERFICIE_FUERA_DE_RANGO: "Superficie fuera de rango",
  FUERA_DE_RANGO: "Fuera de rango",
  ERROR_ALTA_AMBIGUA: "Alta ambigua",
  ERROR_EXCEPCION: "Error inesperado",
  ERROR_SIN_RESPUESTA: "Sin respuesta del servicio",
  // ARCA y errores de servicio
  SINCRONIZADO: "Sincronizado",
  SIN_DATOS: "Sin datos de ARCA",
  ERROR_HTTP: "Error HTTP",
  ERROR_PASO_CORTADO: "Paso cortado (servicio sin respuesta)",
  // BDSJEA-441: actividades (motivos cerrados de resultados_actividades_ejemplo.json)
  PLAN_SIN_CLIENTE: "Plan sin cliente",
  PRODUCTOR_NO_IMPORTADO: "Productor no importado",
  SIN_ESTABLECIMIENTO_DESTINO: "Sin establecimiento de destino",
  CULTIVO_NO_EXISTE: "Cultivo inexistente",
  // BDSJEA-468: stock agrícola. BDSJEA-496: QQ_SOBRE_TOPE, QQ_POR_HA_ALTO y QQ_GRANDE_SIN_HECTAREAS están DEPRECADOS (ya no se emiten: la fila se importa con el aviso
  // QQ_ALTO_REVISAR); se conservan solo para mostrar el historial de importaciones anteriores.
  QQ_SOBRE_TOPE: "Cantidad (QQ) sobre el tope",
  QQ_POR_HA_ALTO: "Cantidad (QQ) por hectárea demasiado alta",
  QQ_GRANDE_SIN_HECTAREAS: "Cantidad (QQ) muy grande sin hectáreas para validar",
  CULTIVO_SIN_MAPEO: "Cultivo sin mapeo",
  STOCK_INVALIDO: "Stock inválido",
  STOCK_FUERA_DE_RANGO: "Stock fuera de rango",
  PLAN_NO_RECIENTE: "Plan que no es el más reciente del productor",
  SIN_FECHA: "Sin fecha",
  PLAN_REPETIDO_SUPERADO: "Plan repetido (superado)",
  SUPERFICIE_INVALIDA: "Superficie inválida",
  SUPERFICIE_CERO: "Superficie cero",
  PLAN_SIN_SUPERFICIE: "Plan sin superficie",
  PRODUCTO_NULO: "Producto nulo",
  PRODUCTO_NO_MAPEADO: "Producto no mapeado",
  PRODUCTO_NO_CONFIRMADO: "Producto no confirmado",
  PRODUCTO_FILTRADO: "Producto filtrado",
  PLAN_INEXISTENTE: "Plan inexistente",
  FILA_INVALIDA: "Fila inválida",
  EXCEPCION: "Error inesperado",
  SIN_FILAS: "Sin filas",
  CABEZAS_INVALIDAS: "Cabezas inválidas",
  SIN_CABEZAS: "Sin cabezas",
  SIN_DETALLE_API: "Sin detalle disponible",
  // Cierre
  OTRO: "Otros",
  OTROS_MOTIVOS: ETIQUETA_OTROS_MOTIVOS,
})

/**
 * Claves cerradas por contexto y su origen.
 * - productores: migracion-bsj/app/importar_productores.py (lineas 122, 141, 145,
 *   151/157, 315, 326-338, 351) y excluidos.py (constantes de resultado).
 * - establecimientos: importar_establecimientos.py (lineas 146, 231-250, 480, 555,
 *   758, 773, 783, 794, 806, 814; resultado FUERA_DE_RANGO en 549).
 * - arca: sincronizar_arca.py (104, 109, 115, 119); el backend normaliza
 *   `arca.motivo` a estas categorias (el texto crudo no viaja).
 */
export const CLAVES_POR_CONTEXTO = Object.freeze({
  productores: Object.freeze([
    "REPETIDO_OTRA_ENTIDAD",
    "REPETIDO_MISMA_ENTIDAD",
    "SIN_RAZON_SOCIAL",
    "CUIT_NO_CARGADO",
    "CUIT_INVALIDO",
    "ENTIDAD_INVALIDA",
    "YA_EXISTIA",
    "ACTUALIZADO",
    "ERROR",
    CLAVE_OTRO,
  ]),
  establecimientos: Object.freeze([
    "REPETIDO_OTRA_ENTIDAD",
    "SALTADO_EXISTENTE",
    "CLIENTE_NO_ENCONTRADO",
    "PLAN_NO_ENCONTRADO",
    "CLIENTE_EXCLUIDO",
    "SIN_PRODUCTOR_MIGRADO",
    "PRODUCTOR_SIN_ENTIDAD",
    "ENTIDAD_INVALIDA",
    "PROVINCIA_NO_RESUELTA",
    "SUPERFICIE_FUERA_DE_RANGO",
    "FUERA_DE_RANGO",
    "ERROR_HTTP",
    "ERROR_SIN_RESPUESTA",
    "ERROR_PASO_CORTADO",
    "ERROR_ALTA_AMBIGUA",
    "ERROR_EXCEPCION",
    CLAVE_OTRO,
  ]),
  arca: Object.freeze([
    "SINCRONIZADO",
    "SIN_DATOS",
    "ERROR_HTTP",
    "ERROR_PASO_CORTADO",
    CLAVE_OTRO,
  ]),
  // BDSJEA-441: claves = `motivo` del vocabulario cerrado de cada paso
  // (contrato 8.3, resultados_actividades_ejemplo.json) + ERROR (resultado sin
  // motivo). Igualdad exacta verificada en vocabularioActividades.test.js. Clave desconocida => OTRO ("Otros motivos").
  siembras: Object.freeze([
    "ENTIDAD_INVALIDA",
    "PLAN_SIN_CLIENTE",
    "PRODUCTOR_NO_IMPORTADO",
    "SIN_ESTABLECIMIENTO_DESTINO",
    "CULTIVO_NO_EXISTE",
    "SIN_FECHA",
    "PLAN_REPETIDO_SUPERADO",
    "SUPERFICIE_INVALIDA",
    "SUPERFICIE_CERO",
    "SUPERFICIE_FUERA_DE_RANGO",
    "PLAN_SIN_SUPERFICIE",
    "PRODUCTO_NULO",
    "PRODUCTO_NO_MAPEADO",
    "PRODUCTO_NO_CONFIRMADO",
    "PRODUCTO_FILTRADO",
    "PLAN_INEXISTENTE",
    "FILA_INVALIDA",
    "EXCEPCION",
    "ERROR",
    CLAVE_OTRO,
  ]),
  ganaderia: Object.freeze([
    "ENTIDAD_INVALIDA",
    "FILA_INVALIDA",
    "PLAN_SIN_CLIENTE",
    "PRODUCTOR_NO_IMPORTADO",
    "SIN_ESTABLECIMIENTO_DESTINO",
    "SIN_FILAS",
    "SIN_DETALLE_API",
    "PLAN_REPETIDO_SUPERADO",
    "EXCEPCION",
    "ERROR",
    CLAVE_OTRO,
  ]),
  tambo: Object.freeze([
    "ENTIDAD_INVALIDA",
    "FILA_INVALIDA",
    "PLAN_SIN_CLIENTE",
    "PRODUCTOR_NO_IMPORTADO",
    "SIN_ESTABLECIMIENTO_DESTINO",
    "SIN_CABEZAS",
    "CABEZAS_INVALIDAS",
    "EXCEPCION",
    "ERROR",
    CLAVE_OTRO,
  ]),
  // BDSJEA-468: motivos del stock agrícola (vocabularioCerrado.stockAgricola.motivo)
  stockAgricola: Object.freeze([
    "ENTIDAD_INVALIDA",
    "PLAN_SIN_CLIENTE",
    "PRODUCTOR_NO_IMPORTADO",
    "PLAN_NO_RECIENTE",
    "STOCK_INVALIDO",
    "QQ_SOBRE_TOPE",
    "QQ_POR_HA_ALTO",
    "QQ_GRANDE_SIN_HECTAREAS",
    "STOCK_FUERA_DE_RANGO",
    "CULTIVO_SIN_MAPEO",
    "CULTIVO_NO_EXISTE",
    "SIN_ESTABLECIMIENTO_DESTINO",
    "SIN_DETALLE_API",
    "EXCEPCION",
    "ERROR",
    CLAVE_OTRO,
  ]),
})

// BDSJEA-441: AVISOS cerrados de las actividades (columna `avisos`: codigos
// separados por ";"). Un aviso NO impide importar la fila: avisa algo a revisar.
// Origen: vocabularioCerrado.<paso>.avisos de resultados_actividades_ejemplo.json
// (el backend normaliza lo desconocido a "OTRO"). Textos claros, sin jerga.
export const ETIQUETA_AVISO_OTRO = "Otro aviso"
const ETIQUETAS_AVISO = Object.freeze({
  // Comunes
  FECHA_ALTERNATIVA: "Se usó una fecha alternativa",
  EST_NO_IMPORTADO: "El establecimiento de origen no se importó",
  EST_ORIGEN_INVALIDO: "El establecimiento de origen no es válido",
  EST_OTRO_PRODUCTOR: "El establecimiento pertenece a otro productor",
  // Siembras
  CAMPANA_POR_FECHA: "Campaña asignada según la fecha",
  CAMPANA_NUEVA: "Se creó una campaña nueva",
  CAMPANA_EQUIVALENTE_EXISTENTE: "Se usó una campaña equivalente ya existente",
  CAMPANA_NORMALIZADA: "Código de campaña acortado (solo cambia el formato)",
  MAQUINARIA_SIN_DATO: "Sin dato de maquinaria",
  PRODUCTO_PROVISORIO: "Producto provisorio",
  RENDIMIENTO_SIN_ZONA: "Rendimiento sin zona del establecimiento",
  RENDIMIENTO_ZONA_NO_CONFIABLE: "Rendimiento de una zona poco confiable",
  RENDIMIENTO_SIN_VALOR_AGRO: "Rendimiento sin valor cargado para la zona",
  PLAN_CON_AMBAS_FUENTES: "Plan con datos de dos fuentes",
  FILA_REPETIDA_EN_PLAN: "Fila repetida en el plan",
  SUPERFICIE_MAYOR_AL_ESTABLECIMIENTO:
    "Superficie mayor a la del establecimiento",
  MODIFICADA_MANUALMENTE: "Modificada manualmente",
  HECTAREAS_TRUNCADAS: "Hectáreas divididas por 10 (se quitó un cero)",
  SIEMBRA_SIN_DESGLOSE_TENENCIA: "Siembra sin desglose de tenencia (propia/arrendada)",
  TENENCIA_NO_CIERRA_CON_TOTAL: "La tenencia propia y arrendada no cierra con el total",
  // BDSJEA-515
  ZONA_DISCREPANTE_ENTRE_ARCHIVOS: "Zona de rendimiento distinta entre archivos",
  NIVEL_RENDIMIENTO_DE_SIEMBRA:
    "Nivel de rendimiento de la siembra tomado de otro archivo o sin dato",
  // Ganadería
  VALOR_INVALIDO: "Valor inválido",
  CABEZAS_OUTLIER: "Cantidad de cabezas fuera de lo habitual",
  TOTAL_FUERA_DE_RANGO: "Total fuera de rango",
  FUSION_INVERNADA_FEEDLOT: "Se unificaron invernada y feedlot",
  FECHA_EJECUCION: "Se usó la fecha de la importación",
  FECHA_FUTURA: "Fecha futura",
  SIN_PARAMETRIA_VALUACION: "Sin parametría de valuación",
  VALUACION_FUERA_DE_RANGO: "Valuación fuera de rango",
  REIMPORTACION_OTRO_PLAN: "Reimportación desde otro plan",
  DESTINO_AGREGADO_A_ESTABLECIMIENTO:
    "El destino se agregó al establecimiento principal (lotes del plan productivo)",
  PRIMERA_UBICACION: "Se asignó la primera ubicación agrícola del plan",
  SIN_DETALLE_STOCK: "Asignado al establecimiento «Sin Detalle»",
  FACTOR_APLICADO: "Se aplicó un factor de conversión a los QQ",
  // BDSJEA-496: se importa igual; el usuario revisa y corrige
  QQ_ALTO_REVISAR: "Cantidad (QQ) alta: se importó igual - revisar y corregir",
  SUPERFICIE_ALTA_REVISAR: "Superficie alta: se importó igual - revisar y corregir",
  LITROS_ALTO_REVISAR: "Litros por vaca altos: se cargó el valor del origen - revisar y corregir",
  // BDSJEA-495
  ASIGNADO_AL_PRIMERO:
    "El plan tenía varias ubicaciones: se asignó al primer establecimiento (revisar)",
  // BDSJEA-507: cabezas comercializadas por año (agro.comercializacion_ganadera)
  COMERCIALIZADAS_SIN_HACIENDA:
    "El plan informa cabezas comercializadas pero no tiene hacienda",
  ACTIVIDAD_PARCIAL_POR_GRUPO_DESCARTADO:
    "Se descartó una actividad parcial del grupo",
  DESTINO_SIN_HAS:
    "Asignado al establecimiento principal sin hectáreas propias (el plan no las informa)",
  // Tambo
  LITROS_VACIO: "Litros sin dato",
  LITROS_FUERA_DE_RANGO: "Litros fuera de rango",
  LITROS_TOTAL_DIVIDIDO_POR_CABEZAS:
    "Litros totales divididos por la cantidad de cabezas",
  LITROS_ATIPICO: "Litros atípicos",
  FECHA_NULA: "Sin fecha",
  SIN_VINCULO_DESTINO: "Sin vínculo con el establecimiento de destino",
  VARIOS_ESTABLECIMIENTOS: "Vinculado a varios establecimientos",
  ACTIVO_MANUAL_PREVALECE: "Prevalece el tambo activo cargado a mano",
  ACTIVO_MANUAL_Y_MIGRADO: "Hay un tambo activo manual y uno migrado",
  REEMPLAZA_ACTIVO_MIGRADO: "Reemplaza a un tambo activo migrado",
  DESACTIVADO_POR_GANADOR: "Desactivado por otro tambo prioritario",
  OTRO: ETIQUETA_AVISO_OTRO,
})

export const AVISOS_POR_CONTEXTO = Object.freeze({
  siembras: Object.freeze([
    "CAMPANA_POR_FECHA",
    "CAMPANA_NUEVA",
    "CAMPANA_EQUIVALENTE_EXISTENTE",
    "CAMPANA_NORMALIZADA",
    "FECHA_ALTERNATIVA",
    "MAQUINARIA_SIN_DATO",
    "EST_NO_IMPORTADO",
    "EST_ORIGEN_INVALIDO",
    "EST_OTRO_PRODUCTOR",
    "PRODUCTO_PROVISORIO",
    "RENDIMIENTO_SIN_ZONA",
    "RENDIMIENTO_ZONA_NO_CONFIABLE",
    "RENDIMIENTO_SIN_VALOR_AGRO",
    "PLAN_CON_AMBAS_FUENTES",
    "FILA_REPETIDA_EN_PLAN",
    "SUPERFICIE_MAYOR_AL_ESTABLECIMIENTO",
    "MODIFICADA_MANUALMENTE",
    "HECTAREAS_TRUNCADAS",
    "SIEMBRA_SIN_DESGLOSE_TENENCIA",
    "TENENCIA_NO_CIERRA_CON_TOTAL",
    "SUPERFICIE_ALTA_REVISAR",
    "ZONA_DISCREPANTE_ENTRE_ARCHIVOS",
    "NIVEL_RENDIMIENTO_DE_SIEMBRA",
  ]),
  ganaderia: Object.freeze([
    "VALOR_INVALIDO",
    "CABEZAS_OUTLIER",
    "TOTAL_FUERA_DE_RANGO",
    "FUSION_INVERNADA_FEEDLOT",
    "FECHA_ALTERNATIVA",
    "FECHA_EJECUCION",
    "FECHA_FUTURA",
    "EST_ORIGEN_INVALIDO",
    "EST_NO_IMPORTADO",
    "EST_OTRO_PRODUCTOR",
    "SIN_PARAMETRIA_VALUACION",
    "VALUACION_FUERA_DE_RANGO",
    "REIMPORTACION_OTRO_PLAN",
    "DESTINO_AGREGADO_A_ESTABLECIMIENTO",
    "DESTINO_SIN_HAS",
    "ASIGNADO_AL_PRIMERO",
    "CAMPANA_POR_FECHA",
    "CAMPANA_NORMALIZADA",
    "COMERCIALIZADAS_SIN_HACIENDA",
    "ACTIVIDAD_PARCIAL_POR_GRUPO_DESCARTADO",
  ]),
  tambo: Object.freeze([
    "LITROS_VACIO",
    "LITROS_FUERA_DE_RANGO",
    "LITROS_ALTO_REVISAR",
    "LITROS_TOTAL_DIVIDIDO_POR_CABEZAS",
    "LITROS_ATIPICO",
    "FECHA_NULA",
    "SIN_VINCULO_DESTINO",
    "VARIOS_ESTABLECIMIENTOS",
    "EST_OTRO_PRODUCTOR",
    "ACTIVO_MANUAL_PREVALECE",
    "ACTIVO_MANUAL_Y_MIGRADO",
    "REEMPLAZA_ACTIVO_MIGRADO",
    "DESACTIVADO_POR_GANADOR",
    "DESTINO_AGREGADO_A_ESTABLECIMIENTO",
    "DESTINO_SIN_HAS",
    "ASIGNADO_AL_PRIMERO",
  ]),
  // BDSJEA-468: vocabularioCerrado.stockAgricola.avisos
  stockAgricola: Object.freeze([
    "PRIMERA_UBICACION",
    "SIN_DETALLE_STOCK",
    "FECHA_ALTERNATIVA",
    "FECHA_EJECUCION",
    "FECHA_FUTURA",
    "EST_ORIGEN_INVALIDO",
    "EST_NO_IMPORTADO",
    "SIN_PARAMETRIA_VALUACION",
    "VALUACION_FUERA_DE_RANGO",
    "DESTINO_SIN_HAS",
    "FACTOR_APLICADO",
    "QQ_ALTO_REVISAR",
  ]),
})

/**
 * Categoria cerrada de un aviso de actividad: el propio codigo si pertenece al
 * vocabulario del paso; cualquier otra cosa => 'OTRO'.
 */
export const claveAviso = (aviso, contexto) =>
  typeof aviso === "string" &&
  (AVISOS_POR_CONTEXTO[contexto] || []).includes(aviso.trim())
    ? aviso.trim()
    : CLAVE_OTRO

/** Etiqueta legible de un aviso (tabla cerrada; desconocido => "Otro aviso"). */
export const etiquetaAviso = (aviso, contexto) =>
  ETIQUETAS_AVISO[claveAviso(aviso, contexto)]

/**
 * Etiquetas legibles de la columna `avisos` (codigos separados por ";"): sin
 * repetidos y en orden de aparicion; vacio/invalido => []. Nunca texto libre.
 */
export const avisosLabels = (avisos, contexto) => {
  if (typeof avisos !== "string") return []
  const vistas = new Set()
  const salida = []
  avisos.split(";").forEach(token => {
    if (!token.trim()) return
    const etiqueta = etiquetaAviso(token, contexto)
    if (!vistas.has(etiqueta)) {
      vistas.add(etiqueta)
      salida.push(etiqueta)
    }
  })
  return salida
}

// Patrones por contexto, sobre el texto normalizado (minusculas, sin tildes).
// El orden importa: el primero que coincide gana.
const PATRONES = Object.freeze({
  productores: [
    ["SIN_RAZON_SOCIAL", /razon social vacia|^sin razon social/],
    ["CUIT_NO_CARGADO", /no tiene cuit cargado|^cuit no cargado/],
    ["CUIT_INVALIDO", /^cuit invalido/],
    ["ENTIDAD_INVALIDA", /^id_entidad invalido/],
    ["REPETIDO_OTRA_ENTIDAD", /ya existe como productor de la entidad/],
    // Variante con codigos de entidad: "CUIT repetido en la entidad BER, ya
    // cargada en la entidad BSJ".
    [
      "REPETIDO_OTRA_ENTIDAD",
      /^cuit repetido en la entidad .*ya cargada en la entidad/,
    ],
    ["REPETIDO_MISMA_ENTIDAD", /repetido.*misma entidad/],
    ["YA_EXISTIA", /^cuit ya existe en la bd destino/],
    ["ACTUALIZADO", /^cuit ya existia/],
    ["ERROR", /^excepcion durante/],
  ],
  establecimientos: [
    ["CLIENTE_EXCLUIDO", /^cliente excluido de la migracion/],
    ["PLAN_NO_ENCONTRADO", /^id_plan .*no encontrado/],
    ["CLIENTE_NO_ENCONTRADO", /^id_cliente .*no encontrado en clientes/],
    ["SIN_PRODUCTOR_MIGRADO", /no tiene productor migrado/],
    ["PRODUCTOR_SIN_ENTIDAD", /no tiene id_entidad/],
    ["ENTIDAD_INVALIDA", /^id_entidad invalido/],
    ["PROVINCIA_NO_RESUELTA", /^provincia_no_resuelta/],
    ["SUPERFICIE_FUERA_DE_RANGO", /^superficie_fuera_de_rango/],
    ["REPETIDO_OTRA_ENTIDAD", /pertenece al productor de la entidad/],
    ["SALTADO_EXISTENTE", /^ya existe el establecimiento/],
    ["ERROR_HTTP", /^fallo http/],
    ["ERROR_PASO_CORTADO", /^paso cortado/],
    ["ERROR_SIN_RESPUESTA", /^sin respuesta de ewan-agro/],
    ["ERROR_ALTA_AMBIGUA", /^alta ambigua/],
    ["ERROR_EXCEPCION", /^excepcion durante/],
  ],
  arca: [
    ["SIN_DATOS", /^arca no devolvio domicilio fiscal ni condicion iva/],
    ["ERROR_PASO_CORTADO", /^paso cortado/],
    ["ERROR_HTTP", /^fallo http/],
  ],
  // BDSJEA-441: las actividades traen solo claves cerradas (el backend ya las
  // normaliza): sin patrones de texto libre, y sin caer en los de otros contextos.
  siembras: [],
  ganaderia: [],
  tambo: [],
  stockAgricola: [],
})

const CONJUNTOS = Object.freeze(
  Object.fromEntries(
    Object.entries(CLAVES_POR_CONTEXTO).map(([c, claves]) => [
      c,
      new Set(claves),
    ]),
  ),
)
const TODAS_LAS_CLAVES = new Set(Object.keys(ETIQUETAS_CLAVE))
// Contexto ausente o desconocido: se prueban los patrones de todos.
const PATRONES_TODOS = Object.freeze([
  ...PATRONES.productores,
  ...PATRONES.establecimientos,
  ...PATRONES.arca,
])

const normalizarTexto = texto =>
  String(texto)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()

/**
 * Clave categorica CERRADA de un motivo (texto libre del CSV).
 * @param {string} motivo texto del motivo (cualquier valor; no string => 'OTRO')
 * @param {"productores"|"establecimientos"|"arca"} [contexto] sin contexto se
 *   prueban todos los patrones
 * @returns {string} una clave de CLAVES_POR_CONTEXTO (MAYUSCULAS_CON_GUION);
 *   nunca texto libre: lo desconocido es 'OTRO'. Un motivo que ya es una clave
 *   cerrada del contexto (p. ej. la que normaliza el backend para ARCA) se
 *   devuelve tal cual.
 */
export const claveMotivo = (motivo, contexto) => {
  if (typeof motivo !== "string") return CLAVE_OTRO
  const bruto = motivo.trim()
  if (!bruto) return CLAVE_OTRO
  const conjunto = CONJUNTOS[contexto]
  const exacta = bruto.toUpperCase()
  if (conjunto ? conjunto.has(exacta) : TODAS_LAS_CLAVES.has(exacta)) {
    return exacta === CLAVE_OTROS_MOTIVOS ? CLAVE_OTRO : exacta
  }
  const texto = normalizarTexto(bruto)
  const patrones = PATRONES[contexto] || PATRONES_TODOS
  for (const [clave, patron] of patrones) {
    if (patron.test(texto)) return clave
  }
  return CLAVE_OTRO
}

/**
 * Etiqueta legible en espanol de una clave (tabla cerrada).
 * @param {string} clave
 * @returns {string} la etiqueta, o 'Otros' si la clave no es conocida
 */
export const etiquetaClave = clave =>
  typeof clave === "string" &&
  Object.prototype.hasOwnProperty.call(ETIQUETAS_CLAVE, clave)
    ? ETIQUETAS_CLAVE[clave]
    : ETIQUETAS_CLAVE[CLAVE_OTRO]

const esFila = fila => fila != null && typeof fila === "object"

/**
 * Clave de una fila: por su `motivo`; si el motivo no se reconoce (o esta vacio)
 * se usa el `resultado` cuando es una clave cerrada del contexto (p. ej. una fila
 * REPETIDO_OTRA_ENTIDAD o FUERA_DE_RANGO sin motivo). Nunca lee otra columna.
 * @param {object} fila
 * @param {"productores"|"establecimientos"|"arca"} [contexto]
 * @returns {string}
 */
export const claveDeFila = (fila, contexto) => {
  if (!esFila(fila)) return CLAVE_OTRO
  const porMotivo = claveMotivo(fila.motivo, contexto)
  if (porMotivo !== CLAVE_OTRO) return porMotivo
  const resultado =
    typeof fila.resultado === "string" ? fila.resultado.trim() : ""
  const conjunto = CONJUNTOS[contexto]
  if (resultado && resultado !== CLAVE_OTRO) {
    if (conjunto ? conjunto.has(resultado) : TODAS_LAS_CLAVES.has(resultado)) {
      return resultado === CLAVE_OTROS_MOTIVOS ? CLAVE_OTRO : resultado
    }
  }
  return CLAVE_OTRO
}

const redondear1 = n => Math.round(n * 10) / 10

/**
 * Agrupa filas por clave categorica de motivo.
 * @param {Array<object>} filas filas del CSV (null/undefined se ignoran)
 * @param {object} [opciones]
 * @param {function(object): string} [opciones.claveFila] fila => clave. Si
 *   devuelve algo fuera de la tabla cerrada se trata como 'OTRO'. Por defecto
 *   claveDeFila(fila, opciones.contexto).
 * @param {"productores"|"establecimientos"|"arca"} [opciones.contexto]
 * @param {number} [opciones.topN=4] categorias conocidas que se muestran
 * @param {string} [opciones.etiquetaOtros='Otros motivos']
 * @returns {Array<{clave:string, etiqueta:string, cantidad:number, pct:number}>}
 *   ordenado por cantidad desc (empate: orden de aparicion). Los TOP-N exactos
 *   mas, SOLO si hay resto, una unica categoria {clave:'OTROS_MOTIVOS'} que suma
 *   el resto y todo lo 'OTRO'. Tope duro: topN + 1 categorias. pct = % sobre las
 *   filas validas (1 decimal).
 */
export const categorizarMotivos = (filas, opciones = {}) => {
  const {
    claveFila,
    contexto,
    topN = TOP_N_POR_DEFECTO,
    etiquetaOtros = ETIQUETA_OTROS_MOTIVOS,
  } = opciones || {}
  if (!Array.isArray(filas)) return []
  const obtener =
    typeof claveFila === "function"
      ? claveFila
      : fila => claveDeFila(fila, contexto)
  const tope = Number.isFinite(topN) ? Math.max(Math.floor(topN), 0) : 4
  const conteos = new Map()
  let total = 0
  let otros = 0
  for (let i = 0; i < filas.length; i += 1) {
    const fila = filas[i]
    if (!esFila(fila)) continue
    total += 1
    let clave
    try {
      clave = obtener(fila)
    } catch {
      clave = CLAVE_OTRO
    }
    if (
      typeof clave !== "string" ||
      !TODAS_LAS_CLAVES.has(clave) ||
      clave === CLAVE_OTRO ||
      clave === CLAVE_OTROS_MOTIVOS
    ) {
      otros += 1
    } else {
      conteos.set(clave, (conteos.get(clave) || 0) + 1)
    }
  }
  const ordenadas = [...conteos.entries()]
    .map(([clave, cantidad], orden) => ({ clave, cantidad, orden }))
    .sort((a, b) => b.cantidad - a.cantidad || a.orden - b.orden)
  const visibles = ordenadas.slice(0, tope)
  const resto =
    otros + ordenadas.slice(tope).reduce((s, c) => s + c.cantidad, 0)
  const pct = cantidad => (total > 0 ? redondear1((cantidad / total) * 100) : 0)
  const salida = visibles.map(({ clave, cantidad }) => ({
    clave,
    etiqueta: etiquetaClave(clave),
    cantidad,
    pct: pct(cantidad),
  }))
  if (resto > 0) {
    salida.push({
      clave: CLAVE_OTROS_MOTIVOS,
      etiqueta: typeof etiquetaOtros === "string" ? etiquetaOtros : "",
      cantidad: resto,
      pct: pct(resto),
    })
  }
  return salida
}

/**
 * Total de PROCESADOS de ARCA discriminado. Usa solo `resultado` y `motivo`
 * (nunca razon_social ni otras columnas).
 * @param {Array<object>} filas
 * @param {object} [opciones] { topN }
 * @returns {{total:number, creados:number, sincronizados:number,
 *   sinDatos:number, noImportados:number, motivos:Array}} `creados` =
 *   `sincronizados` (alias para graficar igual que productores/establecimientos);
 *   `noImportados` = todo lo no SINCRONIZADO (incluye SIN_DATOS); `motivos` =
 *   categorizarMotivos de los no sincronizados (SIN_DATOS, ERROR_HTTP, ...).
 */
export const categorizarArca = (filas, opciones = {}) => {
  const { topN = TOP_N_POR_DEFECTO } = opciones || {}
  const validas = Array.isArray(filas) ? filas.filter(esFila) : []
  let sincronizados = 0
  let sinDatos = 0
  const noSincronizados = []
  validas.forEach(fila => {
    const resultado =
      typeof fila.resultado === "string" ? fila.resultado.trim() : ""
    if (resultado === "SINCRONIZADO") {
      sincronizados += 1
      return
    }
    if (resultado === "SIN_DATOS") sinDatos += 1
    noSincronizados.push(fila)
  })
  const claveFila = fila => {
    const resultado =
      typeof fila.resultado === "string" ? fila.resultado.trim() : ""
    if (resultado === "SIN_DATOS") return "SIN_DATOS"
    return claveMotivo(fila.motivo, "arca")
  }
  return {
    total: validas.length,
    creados: sincronizados,
    sincronizados,
    sinDatos,
    noImportados: noSincronizados.length,
    motivos: categorizarMotivos(noSincronizados, { claveFila, topN }),
  }
}

/**
 * Total PROCESADO discriminado para el grafico. Decision del developer:
 * ACTUALIZADO, YA_EXISTIA y SALTADO_EXISTENTE son importados (no fallo); se
 * cuentan aparte y NO entran en `motivos`.
 * @param {Array<object>} filas
 * @param {object} opciones
 * @param {"productores"|"establecimientos"|"arca"} opciones.contexto
 * @param {number} [opciones.topN=4]
 * @returns {{total:number, creados:number, actualizados:number,
 *   yaExistian:number, noImportados:number, motivos:Array}} total =
 *   creados + actualizados + yaExistian + noImportados. `motivos` =
 *   categorizarMotivos de los no importados. Para contexto 'arca' devuelve
 *   categorizarArca (que ademas trae sincronizados y sinDatos).
 */
export const categorizarProcesados = (filas, opciones = {}) => {
  const { contexto, topN = TOP_N_POR_DEFECTO } = opciones || {}
  if (contexto === "arca") return categorizarArca(filas, { topN })
  const tipo =
    contexto === "establecimientos" ? "establecimientos" : "productores"
  const validas = Array.isArray(filas) ? filas.filter(esFila) : []
  let creados = 0
  let actualizados = 0
  let yaExistian = 0
  const noImportadas = []
  validas.forEach(fila => {
    const resultado =
      typeof fila.resultado === "string" ? fila.resultado.trim() : ""
    if (!clasificarResultado(resultado, tipo).noImportado) {
      if (resultado === "CREADO") creados += 1
      else if (resultado === "ACTUALIZADO") actualizados += 1
      else yaExistian += 1
    } else {
      noImportadas.push(fila)
    }
  })
  return {
    total: validas.length,
    creados,
    actualizados,
    yaExistian,
    noImportados: noImportadas.length,
    motivos: categorizarMotivos(noImportadas, { contexto: tipo, topN }),
  }
}

/**
 * BDSJEA-441: total PROCESADO de una actividad (siembras | ganaderia | tambo).
 * Importados (no son fallo): CREADO, CREADO_HISTORIA (tambo), ACTUALIZADO y los
 * que ya existian (YA_EXISTIA, SALTADO_EXISTENTE). El resto son no importados y
 * se discriminan por `motivo` cerrado (top-N + "Otros motivos").
 * @param {Array<object>} filas
 * @param {object} opciones
 * @param {"siembras"|"ganaderia"|"tambo"} opciones.contexto
 * @param {number} [opciones.topN=4]
 * @returns {{total:number, creados:number, creadosHistoria:number,
 *   actualizados:number, yaExistian:number, noImportados:number,
 *   motivos:Array}}
 */
export const categorizarActividad = (filas, opciones = {}) => {
  const { contexto, topN = TOP_N_POR_DEFECTO } = opciones || {}
  const validas = Array.isArray(filas) ? filas.filter(esFila) : []
  let creados = 0
  let creadosHistoria = 0
  let actualizados = 0
  let yaExistian = 0
  const noImportadas = []
  validas.forEach(fila => {
    const resultado =
      typeof fila.resultado === "string" ? fila.resultado.trim() : ""
    if (clasificarResultado(resultado, contexto).noImportado) {
      noImportadas.push(fila)
    } else if (resultado === "CREADO") creados += 1
    else if (resultado === "CREADO_HISTORIA") creadosHistoria += 1
    else if (resultado === "ACTUALIZADO") actualizados += 1
    else yaExistian += 1
  })
  return {
    total: validas.length,
    creados,
    creadosHistoria,
    actualizados,
    yaExistian,
    noImportados: noImportadas.length,
    motivos: categorizarMotivos(noImportadas, { contexto, topN }),
  }
}

// Observaciones de los establecimientos creados (codigos cerrados del backend).
const ETIQUETAS_OBSERVACION_BASE = Object.freeze({
  SIN_CP: "Sin código postal",
  DEPARTAMENTO_GENERICO_O_NO_RESUELTO: "Departamento genérico/no resuelto",
  POLIGONO_POR_DEFECTO: "Polígono por defecto",
  SUPERFICIE_MENOR_1HA: "Superficie < 1 ha",
  DESTINO_SIN_MAPEO: "Destino sin mapeo",
  // BDSJEA-463: avisos del importador de lotes (el backend los deriva a observaciones)
  LOTE_SIN_SUPERFICIE: "Lote sin superficie (0 ha)",
  TENENCIA_ASUMIDA_PROPIA: "Tenencia asumida propia (el origen no desglosa)",
  LOTE_LEGACY_SIN_PARTIR: "Lote anterior sin partir: limpiar y reimportar",
  // BDSJEA-471: polígonos aproximados de lotes generados por la importación
  POLIGONO_APROXIMADO: "Polígono aproximado: mover al lugar correcto",
  LOTE_SIN_COORDENADA: "Sin coordenada: lote sin polígono",
  POLIGONO_PREEXISTENTE_NO_REGENERADO:
    "Ya tiene polígonos dibujados: no se generaron aproximados",
  POLIGONO_SOLAPA_APROXIMADO:
    "Polígono aproximado solaparía con otro: dibujar a mano",
  // BDSJEA-470: nivel de rendimiento (plan-establecimiento.zona) del establecimiento
  // (en el origen el nivel de rendimiento es el campo `zona` de plan-establecimiento)
  NIVEL_RENDIMIENTO_VACIO:
    "Sin nivel de rendimiento (campo zona vacío en el origen)",
  NIVEL_RENDIMIENTO_NO_RECONOCIDO:
    "Sin nivel de rendimiento (campo zona fuera de 1 a 5 en el origen)",
  NIVEL_RENDIMIENTO_DISTINTO:
    "Niveles de rendimiento distintos en la finca: se tomó el de la primera fila",
})

// BDSJEA-446 / BDSJEA-418: avisos de ubicacion del importador de establecimientos
// (migracion-bsj/app/ubicacion.py, AVISO_*): resolucion de departamento GENERICO por
// codigo postal y de la localidad contra el catalogo. Solo se traducen (display):
// no se ofrecen en el filtro de la tabla hasta que el backend los exponga en la
// columna `observaciones`.
const ETIQUETAS_AVISO_UBICACION = Object.freeze({
  GENERICO_COMPLETADO_POR_CP:
    "Departamento genérico completado según el código postal",
  GENERICO_COMPLETADO_POR_CP_INFERIDO:
    "Departamento genérico completado por inferencia del código postal",
  GENERICO_SIN_CP_VERIFICABLE:
    "Departamento genérico sin código postal verificable",
  GENERICO_CAMBIA_ZONA: "Al resolver el departamento genérico cambia la zona",
  DEPARTAMENTO_COMPLETADO_DESDE_CATALOGO:
    "Departamento completado desde el catálogo",
  LOCALIDAD_COMPLETADA_DESDE_CATALOGO: "Localidad completada desde el catálogo",
  LOCALIDAD_EN_OTRO_DEPARTAMENTO: "La localidad está en otro departamento",
  LOCALIDAD_CAMBIA_ZONA: "Al resolver la localidad cambia la zona",
  LOCALIDAD_AMBIGUA: "Localidad ambigua",
  LOCALIDAD_NO_RESUELTA: "Localidad no resuelta",
  LOCALIDAD_CP_NO_ASOCIADO: "Código postal no asociado a la localidad",
  LOCALIDAD_SIN_DEPARTAMENTO_EN_CATALOGO:
    "Localidad sin departamento en el catálogo",
  LOCALIDAD_CP_COMPARTIDO_NOMBRES_DISTINTOS:
    "Código postal compartido por localidades de nombre distinto",
  LOCALIDAD_CP_COMPARTIDO_SIN_COINCIDENCIA:
    "Código postal compartido sin localidad coincidente",
})

export const CODIGOS_AVISO_UBICACION = Object.freeze(
  Object.keys(ETIQUETAS_AVISO_UBICACION),
)

const ETIQUETAS_OBSERVACION = Object.freeze({
  ...ETIQUETAS_OBSERVACION_BASE,
  ...ETIQUETAS_AVISO_UBICACION,
})

// Observaciones cerradas {codigo, etiqueta}, en el orden de la tabla (para armar
// el select de filtro sin repetir los codigos en la pantalla).
export const OBSERVACIONES_CERRADAS = Object.freeze(
  Object.entries(ETIQUETAS_OBSERVACION_BASE).map(([codigo, etiqueta]) =>
    Object.freeze({ codigo, etiqueta }),
  ),
)

/**
 * Codigos cerrados (conocidos) de una columna "observaciones".
 * @param {string|string[]} codigos separados por coma/';' o arreglo
 * @returns {string[]} codigos en mayusculas, sin repetidos ni desconocidos
 */
export const observacionesCodigos = codigos => {
  let lista = []
  if (Array.isArray(codigos)) lista = codigos
  else if (typeof codigos === "string") lista = codigos.split(/[,;]/)
  const vistos = new Set()
  lista.forEach(c => {
    if (typeof c !== "string") return
    const codigo = c.trim().toUpperCase()
    if (Object.prototype.hasOwnProperty.call(ETIQUETAS_OBSERVACION, codigo))
      vistos.add(codigo)
  })
  return [...vistos]
}

/**
 * Etiquetas cortas de la columna "Observaciones".
 * @param {string|string[]} codigos codigos cerrados separados por coma (tambien
 *   se tolera ';') o un arreglo de codigos
 * @returns {string[]} etiquetas en el orden recibido, sin repetidos; los codigos
 *   desconocidos se ignoran (nunca texto libre); vacio/invalido => []
 */
export const observacionesLabels = codigos => {
  let lista = []
  if (Array.isArray(codigos)) lista = codigos
  else if (typeof codigos === "string") lista = codigos.split(/[,;]/)
  const vistos = new Set()
  const salida = []
  lista.forEach(c => {
    if (typeof c !== "string") return
    const codigo = c.trim().toUpperCase()
    if (
      !Object.prototype.hasOwnProperty.call(ETIQUETAS_OBSERVACION, codigo) ||
      vistos.has(codigo)
    ) {
      return
    }
    vistos.add(codigo)
    salida.push(ETIQUETAS_OBSERVACION[codigo])
  })
  return salida
}

/**
 * Igual que observacionesLabels pero como un texto "a, b" ("" si no hay).
 * @param {string|string[]} codigos
 * @returns {string}
 */
export const observacionesTexto = codigos =>
  observacionesLabels(codigos).join(", ")
